import { useState, useCallback } from 'react';
import { newsletterService, NewsletterResponse } from '@/lib/newsletter';

interface UseNewsletterFormOptions {
  source?: string;
  onSuccess?: (response: NewsletterResponse) => void;
  onError?: (response: NewsletterResponse) => void;
}

interface UseNewsletterFormReturn {
  email: string;
  setEmail: (email: string) => void;
  isLoading: boolean;
  isSubscribed: boolean;
  message: string;
  error: string;
  subscribe: () => Promise<void>;
  reset: () => void;
}

export function useNewsletterForm(options: UseNewsletterFormOptions = {}): UseNewsletterFormReturn {
  const [email, setEmail] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [isSubscribed, setIsSubscribed] = useState(false);
  const [message, setMessage] = useState('');
  const [error, setError] = useState('');

  const subscribe = useCallback(async () => {
    if (!email.trim()) {
      setError('Please enter your email address');
      return;
    }

    setIsLoading(true);
    setError('');
    setMessage('');

    try {
      const response = await newsletterService.subscribe({
        email: email.trim(),
        source: options.source
      });

      if (response.success) {
        setIsSubscribed(true);
        setMessage(response.message);
        setEmail(''); // Clear the form
        // Don't set localStorage here - let the component handle it after animation
        // This allows the "Thank you" message to show properly
        options.onSuccess?.(response);
      } else {
        setError(response.message);
        options.onError?.(response);
      }
    } catch (_err) {
      const errorMessage = 'Something went wrong. Please try again.';
      setError(errorMessage);
      options.onError?.({ success: false, message: errorMessage });
    } finally {
      setIsLoading(false);
    }
  }, [email, options]);

  const reset = useCallback(() => {
    setEmail('');
    setIsLoading(false);
    setIsSubscribed(false);
    setMessage('');
    setError('');
  }, []);

  return {
    email,
    setEmail,
    isLoading,
    isSubscribed,
    message,
    error,
    subscribe,
    reset
  };
}